/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
import React from 'react';
//materials
import SortableItem from 'material/sortablelist/sortableitem/sortableitem';
import ErrorMessage from 'material/form/field/errormessage/errormessage';
// utils
import { generateClasses } from 'utils/generators';
// styles
import injectSheet from 'react-jss';
import styles from 'material/sortablelist/sortablelist_styles';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';


/* READ BEFORE IMPLEMENTING:
Components that implement this component must provide a handleChange function as a prop which handles selections/deselections of drag item checkboxes;
this function will be called with the new value being selected/deselcted along with the current order of items which may or may not be utilized. 
For dragEnd events, the parent function handleOrderChange will be called with the new item list (order changed).
These functions are ideal for logic used to calculate next order/checked state of the items passed to sortablelist. 
For an example implementation of the handleChange and handleOrderChange functions, see pages/dashboard/reports/details/dimensions/dimensions.js
*/
/* ========== ~~~~~~~~~~ SORTABLE : CHILD ~~~~~~~~~~ ========== */
// PRESENTATIONAL COMPONENT
// receives app state & actions as props from parent "_container" component
// this child component handles local state and uses props passed from parent to read/write application state when necessary
const Sortable = (props) => {

	//Function to render item list based on selected props and items passed from parent. 

	const getItems = () => {
		if (props.order.length === 0) {
			return generateItems(getInitialOrder());
		} else {
			return generateItems(props.order);
		}
	}

	const generateItems = (orderedList) => (orderedList.map((value, index) => (
		<Draggable key={value} draggableId={value} index={index}>
				{(provided) => (
					<div ref={provided.innerRef}
						{...provided.draggableProps}
						{...provided.dragHandleProps}>
						<SortableItem
							key={index}
							{...props}
							handleSelect={handleSelect}
							item={props.items.filter(item => item.value === value)} />
					</div>
				)}
			</Draggable>
	)))
	

	//Get initial order is called to place the selected items at the top of the list in the order they appear in,
	// upon the mounting of the sortable list component and other cases where props.order is empty (default).  

	const getInitialOrder = () => {
		if (props.selected.length === 0) {
			return transpileItems(props.items);
		} else {
			let initialOrder = Array.from(props.items);
			let selectedOrder = [];
			for (let i = 0; i < props.selected.length; i++) {
				for (let k = 0; k < initialOrder.length; k++) {
					if (initialOrder[k].value === props.selected[i]) {
						selectedOrder.push(initialOrder[k]);
					}
				}
			}
			return transpileItems(selectedOrder.concat(initialOrder.filter((currentValue) => !selectedOrder.includes(currentValue))));
		}
	}

	//Function to call handleChange function in parent upon sortable item being selected/deselected

	const handleSelect = (value) => {
		if (props.order.length === 0){
			props.handleChange(getInitialOrder(), value);
		} else {
			props.handleChange(props.order, value);
		}
	}

	//Function which reorders given list by swapping items at the specified starting and ending indices. 

	const reorder = (list, startIndex, endIndex) => {
		//remove the reordered item from previous position
		const result = Array.from(list);
		const [removed] = result.splice(startIndex, 1);
		//insert the reordered item into the new position
		result.splice(endIndex, 0, removed);
		return result;
	};

	//Callback function for item drag end, reorders items using reorder function above and assigns to this.props.order, by calling
	//handleOrderChange function with new ordered list of all itemn. 

	const onDragEnd = (result) => {
		if (!result.destination) {
			return;
		}
		const items = reorder(
			props.order.length === 0 ? getInitialOrder() : props.order,
			result.source.index,
			result.destination.index
		);
		props.handleOrderChange(items);
	}

	//Transpile items returns the list of values corresponding to the given list of items.

	const transpileItems = (itemList) => {
		let returnedArray = []
		for (let i = 0; i < itemList.length; i++) {
			returnedArray[i] = itemList[i].value;
		}
		return returnedArray;
	}

	return (
		<div className={`sortable ${generateClasses(props)} ${props.classes.sortable}`}>
			<DragDropContext onDragEnd={onDragEnd}>
				<Droppable droppableId="droppable">
					{(provided) => (
						<div {...provided.droppableProps}
							ref={provided.innerRef}>
							{getItems()}
						</div>
					)}
				</Droppable>
			</DragDropContext>
			{props.error.enabled ? <ErrorMessage klass={'errormessage'}>{props.error.message}</ErrorMessage> : null}
		</div>
	);
};


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default injectSheet(styles)(Sortable);